<template>
  <modal :name="name" :width="520" height="auto" :adaptive="true" :scrollable="true" :focus-trap="true" :clickToClose="false" @before-open="initialize" @before-close="beforeClose" @opened="focusHeading" @closed="$emit('closed')">
    <div class="popup booking-payment-modal" :aria-busy="saving" @keydown.esc.prevent.stop="close">
      <button class="popup__close" type="button" aria-label="Close Record Previous Payment" :disabled="saving" @click="close" />
      <h2 ref="heading" class="popup__title" tabindex="-1">Record Previous Payment</h2>
      <div class="popup__body">
        <form novalidate @submit.prevent="submit">
          <fieldset class="booking-payment-modal__fields" :disabled="saving || uncertain">
            <app-form-group label="Amount" :label-for="`${name}-amount`" required>
              <app-input :id="`${name}-amount`" v-model="draft.amount" name="amount" inputmode="decimal" placeholder="0.00" :error="Boolean(fieldErrors.amount)" @input="clearError('amount')" />
              <template #error><div v-if="fieldErrors.amount">{{ fieldErrors.amount }}</div></template>
            </app-form-group>
            <app-form-group label="Occured At" :label-for="`${name}-occured-date`" required>
              <booking-deadline-input :id="`${name}-occured`" v-model="draft.occured_at" label="Occured At" :disabled="saving || uncertain" :error="Boolean(fieldErrors.occured_at)" @input-valid="clearError('occured_at')" @input-error="invalidDate" />
              <template #error><div v-if="fieldErrors.occured_at">{{ fieldErrors.occured_at }}</div></template>
            </app-form-group>
            <app-form-group label="Description" :label-for="`${name}-description`" required class="booking-payment-modal__description">
              <app-textarea :id="`${name}-description`" v-model="draft.description" name="description" placeholder="Enter description" :error="Boolean(fieldErrors.description)" @input="clearError('description')" />
              <template #error><div v-if="fieldErrors.description">{{ fieldErrors.description }}</div></template>
            </app-form-group>
          </fieldset>
          <p v-if="uncertain" class="booking-payment-modal__hint" role="status">The save result could not be verified. Reload the ledger and check for the payment before recording it again.</p>
          <footer class="booking-payment-modal__footer">
            <app-button theme="dark" :disabled="saving" @click.native="close">Cancel</app-button>
            <app-button v-if="uncertain" @click.native="refreshLedger">Reload ledger</app-button>
            <app-button v-else type="submit" :disabled="saving">{{ saving ? 'Saving…' : 'Save' }}</app-button>
          </footer>
        </form>
      </div>
    </div>
  </modal>
</template>

<script>
import { postFlightsBookingInvoiceLedger } from '@/http'
import BookingDeadlineInput from '@/components/flights/BookingDeadlineInput.vue'
import { responseError } from './booking-detail'
import { previousPaymentErrors, previousPaymentPayload } from './booking-previous-payment'

export default {
  name: 'BookingPreviousPaymentModal',
  components: { BookingDeadlineInput },
  props: { name: { type: String, required: true }, booking: { type: Object, required: true } },
  data() {
    return { draft: { amount: '', occured_at: '', description: '' }, fieldErrors: {}, dateError: '', saving: false, uncertain: false, sessionId: 0 }
  },
  beforeDestroy() { this.sessionId++ },
  methods: {
    initialize() {
      this.sessionId++
      this.draft = { amount: '', occured_at: '', description: '' }
      this.fieldErrors = {}
      this.dateError = ''
      this.saving = false
      this.uncertain = false
    },
    clearError(field) {
      this.$delete(this.fieldErrors, field)
      if (field === 'occured_at') this.dateError = ''
    },
    invalidDate() { this.dateError = 'Enter a valid date and time.' },
    async submit() {
      if (this.saving || this.uncertain || !this.booking.id) return
      this.fieldErrors = previousPaymentErrors(this.draft)
      if (this.dateError) this.$set(this.fieldErrors, 'occured_at', this.dateError)
      if (Object.keys(this.fieldErrors).length) return
      const sessionId = this.sessionId
      this.saving = true
      try {
        await postFlightsBookingInvoiceLedger(this.booking.id, previousPaymentPayload(this.draft), { localErrors: true })
        if (sessionId !== this.sessionId) return
        this.$notify({ type: 'success', title: 'Previous payment recorded.' })
        this.$emit('recorded')
        this.saving = false
        this.close()
      } catch (error) {
        if (sessionId === this.sessionId) {
          this.$notify({ type: 'error', title: responseError(error, 'Unable to record previous payment.') })
          this.uncertain = !error.response || error.response.status >= 500
        }
      } finally { if (sessionId === this.sessionId) this.saving = false }
    },
    refreshLedger() {
      this.$emit('refresh')
      this.close()
    },
    focusHeading() { if (this.$refs.heading) this.$refs.heading.focus() },
    close() { if (!this.saving) this.$modal.hide(this.name) },
    beforeClose(event) {
      if (this.saving) event.cancel()
      else this.sessionId++
    }
  }
}
</script>

<style lang="scss" scoped>
.booking-payment-modal .popup__title { margin-bottom: 20px; padding-right: 28px; text-align: left; cursor: default; }
.booking-payment-modal__fields { min-width: 0; margin: 0; padding: 0; border: 0; }
.booking-payment-modal__fields:disabled { opacity: .7; }
.booking-payment-modal__description { margin-bottom: 0; }
.booking-payment-modal__hint { margin: 16px 0 0; color: $color-text-second; font-size: 13px; }
.booking-payment-modal__footer { display: flex; justify-content: flex-end; flex-wrap: wrap; gap: 12px; margin-top: 24px; }
.booking-payment-modal__footer .btn { min-width: 100px; }
.booking-payment-modal .popup__close:focus-visible { outline: 2px solid $color-theme; outline-offset: 3px; }
</style>
